import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useBackendApi from '../../hooks/useBackendApi';
import useStateWithInputEventHandler from '../../hooks/useStateWithInputEventHandler';
import { IReviewDimension } from '../../types/reviewFormData';
import { replaceUrlParamsWithValue } from '../../utils';
import {
  IInputField,
  IReviewFormState,
} from '../ReviewFormPage/useReviewFormPageData';

const formFieldMapping: {
  [type: string]: { [type: string]: IInputField };
} = {
  provider: {
    feedback: {
      type: 'textInput',
      name: 'publicFeedback',
      label: 'Describe your experience',
      description: "Your review will be public on {{providerName}}'s profile",
      isRequired: true,
      value: '',
    },
  },
  customer: {
    feedback: {
      type: 'textInput',
      name: 'publicFeedback',
      label: 'Describe your experience',
      description: "Your review will be public on {{customerName}}'s profile",
      isRequired: true,
      value: '',
    },
  },
};
export default function useReviewFromPastCustomerPageData() {
  const { reviewId, profileId, categorySlug, key } = useParams<{
    reviewId?: string;
    profileId: string;
    categorySlug: string;
    key: string;
  }>();

  const [customerDetails, handleCustomerDetailsChange] =
    useStateWithInputEventHandler({
      name: '',
      mobile: '',
      email: '',
      reviewDate: '',
    });

  const history = useHistory();

  const { get, post, patch } = useBackendApi();

  const [providerDetails, setProviderDetails] = React.useState<any>({});

  const [response, setResponse] = React.useState<any>();

  const [state, setState] = React.useState<IReviewFormState>({
    customer: { name: '' },
    provider: { name: '', photo: '' },
    formFields: [],
    message: '',
    trafficControlFieldName: '',
  });

  const [userInput, setUserInput] = React.useState<any>({
    flayrFeedback: '',
    media: [],
    publicFeedback: '',
  });

  function handleInputChange(e) {
    const { name, value, type } = e.target;
    switch (type) {
      case 'radio':
        const newData = { ...userInput, [name]: +value };
        saveData({ updating: 'reviewDimension', id: name, score: +value });
        return setUserInput(newData);
      default:
        return setUserInput({ ...userInput, [name]: value });
    }
  }

  React.useEffect(() => {
    if (profileId && categorySlug) {
      const fetch = async () => {
        const url = replaceUrlParamsWithValue(
          '/v1/reviews/@/:profileId/:categorySlug',
          {
            categorySlug,
            profileId,
          }
        );
        const res = await get(url);
        setProviderDetails(res.data.provider);
      };
      fetch();
    }
  }, [profileId, categorySlug]);

  React.useEffect(() => {
    async function fetchReviewFormData() {
      const url = replaceUrlParamsWithValue(`/v1/reviews/invited/:id/:key`, {
        id: reviewId,
        key,
      });
      const res: {
        data: {
          reviewDimensions: IReviewDimension[];
          customer: any;
          provider: any;
        };
      } = await get(url);
      setResponse(res);
    }
    if (reviewId && key) {
      fetchReviewFormData();
    }
  }, [reviewId]);

  React.useEffect(() => {
    if (response) {
      if (!response.message) {
        let formFields: IInputField[] = [
          {
            ...formFieldMapping['provider'].feedback,
            value: response.data.publicFeedback,
          },
        ];

        formFields = [
          ...formFields,
          ...(response.data.reviewDimensions?.map(
            (rd) =>
              ({
                label: rd.label,
                name: rd.id,
                description: rd.description,
                isRequired: rd.isRequired,
                isTrafficControl: rd.isTrafficControl,
                type: rd.kind,
                value: rd.score,
              } as IInputField)
          ) || []),
          {
            type: 'media',
            name: 'media',
            label: 'Add photos to your review (optional)',
            description:
              'Would you be able to upload a picture? Your photos helps others make the best decisions for the service.',
            value: [],
          },
        ];
        const { customer, provider, flayrFeedback, publicFeedback } =
          response.data;
        const trafficControlFieldName = response.data.reviewDimensions?.find(
          (rd) => rd.isTrafficControl
        ).id;
        setState({ formFields, customer, provider, trafficControlFieldName });
        let userInput: any = {};

        formFields.map((ff) => (userInput[ff.name] = ff.value));
        userInput = {
          ...userInput,
          flayrFeedback,
          publicFeedback,
        };
        setUserInput(userInput);
      } else {
        setState({ ...state, message: response.message });
      }
    }
  }, [response]);

  function handleImageUpload(url) {
    const mediaUrls = [url, ...userInput.media];
    setUserInput({ ...userInput, media: mediaUrls });
  }

  function handleDeleteImg(e) {
    const { id } = e.target;
    const newMedia = userInput.media.filter((_, idx) => idx !== +id);
    setUserInput({ ...userInput, media: newMedia });
  }

  function handleInputBlur(e) {
    const { name, value } = e.target;
    saveData({ updating: name, comment: value });
  }

  function saveData(data) {
    const url = replaceUrlParamsWithValue('/v1/reviews/invited/:id/:key', {
      id: reviewId,
      key,
    });
    return patch(url, data, { loader: false });
  }

  async function handleSubmit(data) {
    const url = replaceUrlParamsWithValue('/v1/reviews/invited/:id/:key', {
      id: reviewId,
      key,
    });
    const mediaUrls = (data?.media && data.media) || userInput.media || [];
    await saveData({ updating: 'media', mediaUrls });
    const res = await post(url);
    setResponse(res);
  }

  async function handleSubmitCustomerDetails() {
    const url = replaceUrlParamsWithValue(
      '/v1/reviews/@/:profileId/:categorySlug',
      {
        profileId,
        categorySlug,
      }
    );
    const data = customerDetails;
    console.log({ url, data });
    const res = await post(url, data);
    history.push(
      `/@/${profileId}/${categorySlug}/reviews/${res.data.id}/${res.data.key}`
    );
  }

  return {
    reviewId,
    customerDetails,
    handleCustomerDetailsChange,
    handleSubmitCustomerDetails,
    providerDetails,
    //
    userInput,
    handleInputChange,
    handleImageUpload,
    handleSubmit,
    handleInputBlur,
    handleDeleteImg,
    saveData,
    submittedMsg: response?.message,
    ...state,
  };
}
